import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import type { TumUtilityAccount } from '@/types/tum';

export const useTumUtilityAccounts = () => {
  const workspace = useWorkspace();
  const currentWorkspace = workspace?.currentWorkspace;
  const queryClient = useQueryClient();

  const { data: accounts, isLoading, error } = useQuery({
    queryKey: ['tum-utility-accounts', currentWorkspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tum_utility_accounts')
        .select(`
          *,
          property:tum_properties(*),
          vendor:tum_utility_vendors(*),
          meter:tum_meters(*)
        `)
        .eq('workspace_id', currentWorkspace!.id)
        .order('account_number', { ascending: true });
      
      if (error) throw error;
      return data as TumUtilityAccount[];
    },
    enabled: !!currentWorkspace?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (account: Omit<TumUtilityAccount, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by' | 'workspace_id' | 'property' | 'vendor' | 'meter'>) => {
      const { data, error } = await supabase
        .from('tum_utility_accounts')
        .insert({ ...account, workspace_id: currentWorkspace!.id })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-utility-accounts'] });
      toast.success('Utility account created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create utility account: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TumUtilityAccount> & { id: string }) => {
      const { data, error } = await supabase
        .from('tum_utility_accounts')
        .update(updates)
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-utility-accounts'] });
      toast.success('Utility account updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update utility account: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tum_utility_accounts')
        .delete()
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-utility-accounts'] });
      toast.success('Utility account deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete utility account: ${error.message}`);
    },
  });

  return {
    accounts: accounts || [],
    isLoading,
    error,
    createAccount: createMutation.mutate,
    updateAccount: updateMutation.mutate,
    deleteAccount: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
